import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { eur, num, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ArrowDownRight, FileDown, FileSpreadsheet, ChevronRight, TrendingUp, Wallet, AlertTriangle, Percent, RefreshCw } from "lucide-react";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

const Delta = ({ c }) => {
  if (!c || c.pct === null || c.pct === undefined) return null;
  const up = c.pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium rounded-full px-1.5 py-0.5 ${up ? "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)]" : "text-destructive bg-destructive/10"}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(c.pct)}%
    </span>
  );
};

const SummaryCard = ({ label, value, cmp, hint, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <Delta c={cmp} />
    </div>
    <div className="mt-1.5 font-heading text-2xl font-semibold tabular">{value}</div>
    {cmp && cmp.previous !== undefined && <div className="mt-1 text-[11px] text-muted-foreground">Prec: {typeof cmp.previous === "number" && String(label).match(/clienti|appunt|vendite/i) ? num(cmp.previous) : eur(cmp.previous)}</div>}
    {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
  </Card>
);

export default function Report() {
  const [period, setPeriod] = useState("month");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [range, setRange] = useState({ start: new Date().toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) });
  const [compare, setCompare] = useState("previous");
  const [source, setSource] = useState("all");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("riepilogo");
  const [drill, setDrill] = useState(null);
  const [mship, setMship] = useState(null);
  useEffect(() => { api.get("/reports/memberships").then((r) => setMship(r.data)).catch(() => {}); }, []);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams({ period, compare, source });
    if (period === "day") p.set("date", date);
    if (period === "custom") { p.set("start", range.start); p.set("end", range.end); }
    return p.toString();
  }, [period, compare, date, range, source]);

  const load = useCallback(async () => {
    setData(null); setError(false);
    const q = buildQuery();
    // Retry automatico (fino a 2 tentativi) per assorbire blip temporanei del
    // backend (es. riavvii/deploy) prima di mostrare l'errore all'utente.
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await api.get(`/reports/advanced?${q}`);
        setData(r.data);
        return;
      } catch (e) {
        lastErr = e;
        if (attempt === 0) await new Promise((res) => setTimeout(res, 1200));
      }
    }
    setError(true);
    const detail = lastErr?.response?.data?.detail;
    toast.error(detail ? `Errore report: ${detail}` : "Errore caricamento report");
  }, [buildQuery]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (svcName) => {
    const p = new URLSearchParams({ service: svcName, period });
    if (period === "day") p.set("date", date);
    if (period === "custom") { p.set("start", range.start); p.set("end", range.end); }
    const { data: dd } = await api.get(`/reports/service-detail?${p.toString()}`);
    setDrill(dd);
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [["Servizio", "Categoria", "Quantita", "Lordo", "Sconti", "Netto", "Ticket medio"]];
    data.services.forEach((s) => rows.push([s.name, s.category || "", s.qty, s.gross, s.discount, s.net, s.avg_ticket]));
    rows.push([]); rows.push(["Riepilogo"]);
    Object.entries(data.summary).forEach(([k, v]) => rows.push([k, v]));
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `report-hair-identity-${period}.csv`; a.click();
    toast.success("CSV esportato");
  };
  const exportPDF = () => { toast.info("Apertura stampa/PDF…"); setTimeout(() => window.print(), 300); };

  const s = data?.summary;
  const pieData = data ? Object.entries(data.payment_methods).map(([name, value]) => ({ name, value })) : [];
  const cmpk = data?.comparison?.kpis || {};

  return (
    <div data-testid="report-page">
      <PageHeader title="Report" subtitle="Reportistica gestionale · vendite, servizi, sconti, team">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]" data-testid="report-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Oggi</SelectItem>
              <SelectItem value="day">Giorno specifico</SelectItem>
              <SelectItem value="week">Settimana</SelectItem>
              <SelectItem value="month">Mese</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="year">Anno</SelectItem>
              <SelectItem value="custom">Personalizzato</SelectItem>
            </SelectContent>
          </Select>
          {period === "day" && <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[150px]" data-testid="report-date" />}
          {period === "custom" && (
            <>
              <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-[145px]" data-testid="report-start" />
              <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-[145px]" data-testid="report-end" />
            </>
          )}
          <Select value={compare} onValueChange={setCompare}>
            <SelectTrigger className="w-[160px]" data-testid="report-compare"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="previous">vs Periodo prec.</SelectItem>
              <SelectItem value="year">vs Anno prec.</SelectItem>
              <SelectItem value="none">Nessun confronto</SelectItem>
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[150px]" data-testid="report-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dati</SelectItem>
              <SelectItem value="native">Solo nativi</SelectItem>
              <SelectItem value="imported">Solo importati</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv"><FileSpreadsheet className="h-4 w-4 mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </PageHeader>

      {error ? (
        <div className="h-64 flex flex-col items-center justify-center gap-3 text-center" data-testid="report-error">
          <AlertTriangle className="h-8 w-8 text-[hsl(var(--hi-status-danger))]" />
          <p className="text-sm text-muted-foreground">Impossibile caricare il report per il periodo selezionato.</p>
          <Button onClick={load} data-testid="report-retry"><RefreshCw className="h-4 w-4 mr-2" />Riprova</Button>
        </div>
      ) : !data ? <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div> : (
        <div className="space-y-6" id="report-printable">
          {/* KPI principali */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <SummaryCard testid="kpi-total-sold" label="Totale venduto" value={eur(s.total_sold)} cmp={cmpk.total_sold} />
            <SummaryCard testid="kpi-collected" label="Incassato" value={eur(s.total_collected)} cmp={cmpk.total_collected} />
            <SummaryCard testid="kpi-services-net" label="Servizi netti" value={eur(s.services_net)} cmp={cmpk.services_net} />
            <SummaryCard testid="kpi-products-net" label="Retail netto" value={eur(s.products_net)} cmp={cmpk.products_net} />
            <SummaryCard testid="kpi-avg-ticket" label="Ticket medio" value={eur(s.avg_ticket)} cmp={cmpk.avg_ticket} />
            <SummaryCard testid="kpi-clients" label="Clienti" value={num(s.n_clients)} cmp={cmpk.n_clients} hint={`${num(s.new_clients)} nuovi`} />
          </div>

          {/* Saldo Cliente: insoluti & crediti */}
          {data.balance && (
            <Card className="p-5" data-testid="report-balance-section">
              <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Wallet className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Saldo clienti — Insoluti & Crediti</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard testid="bal-insoluti-created" label="Insoluti creati" value={eur(data.balance.insoluti_created)} hint={`${num(data.balance.n_debts_created)} nel periodo`} />
                <SummaryCard testid="bal-insoluti-collected" label="Insoluti incassati" value={eur(data.balance.insoluti_collected)} hint="nel periodo" />
                <SummaryCard testid="bal-insoluti-open" label="Insoluti aperti" value={eur(data.balance.insoluti_open_total)} hint="totale attuale" />
                <SummaryCard testid="bal-credit-generated" label="Crediti generati" value={eur(data.balance.credit_generated)} hint="nel periodo" />
                <SummaryCard testid="bal-credit-used" label="Crediti utilizzati" value={eur(data.balance.credit_used)} hint="nel periodo" />
                <SummaryCard testid="bal-credit-available" label="Credito disponibile" value={eur(data.balance.credit_available_total)} hint="totale attuale" />
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground">Gli insoluti incassati rientrano negli <b>incassi</b> del giorno del pagamento ma non nel fatturato servizi (già conteggiato). I crediti utilizzati scalano dal saldo cliente senza creare un nuovo incasso.</div>
            </Card>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="riepilogo" data-testid="rtab-riepilogo">Riepilogo</TabsTrigger>
              <TabsTrigger value="servizi" data-testid="rtab-servizi">Servizi</TabsTrigger>
              <TabsTrigger value="sconti" data-testid="rtab-sconti">Sconti</TabsTrigger>
              <TabsTrigger value="retail" data-testid="rtab-retail">Retail & Pagamenti</TabsTrigger>
              <TabsTrigger value="incassi" data-testid="rtab-incassi">Incassi</TabsTrigger>
              <TabsTrigger value="margini" data-testid="rtab-margini">Margini</TabsTrigger>
              <TabsTrigger value="team" data-testid="rtab-team">Team</TabsTrigger>
              <TabsTrigger value="pacchetti" data-testid="rtab-pacchetti">Pacchetti</TabsTrigger>
              {data.monthly && <TabsTrigger value="annuale" data-testid="rtab-annuale">Annuale</TabsTrigger>}
            </TabsList>

            {/* PACCHETTI & ABBONAMENTI */}
            <TabsContent value="pacchetti" className="mt-4" data-testid="rtab-pacchetti-content">
              {!mship ? <Card className="p-6 text-sm text-muted-foreground">Caricamento…</Card> : (
                <div className="space-y-5">
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-3">Pacchetti</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryCard testid="mk-pkg-sold" label="Venduti" value={num(mship.packages.sold)} />
                      <SummaryCard testid="mk-pkg-active" label="Attivi" value={num(mship.packages.active)} />
                      <SummaryCard testid="mk-pkg-expiring" label="In scadenza (30gg)" value={num(mship.packages.expiring_30d)} />
                      <SummaryCard testid="mk-pkg-expired" label="Scaduti" value={num(mship.packages.expired)} />
                      <SummaryCard testid="mk-pkg-revenue" label="Ricavo" value={eur(mship.packages.revenue)} />
                      <SummaryCard testid="mk-pkg-sess-used" label="Sedute usate" value={num(mship.packages.sessions_used)} hint={`su ${num(mship.packages.sessions_total)}`} />
                      <SummaryCard testid="mk-pkg-sess-left" label="Sedute residue" value={num(mship.packages.sessions_left)} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-3">Abbonamenti</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryCard testid="mk-sub-sold" label="Venduti" value={num(mship.subscriptions.sold)} />
                      <SummaryCard testid="mk-sub-active" label="Attivi" value={num(mship.subscriptions.active)} />
                      <SummaryCard testid="mk-sub-expiring" label="In scadenza (30gg)" value={num(mship.subscriptions.expiring_30d)} />
                      <SummaryCard testid="mk-sub-revenue" label="Ricavo" value={eur(mship.subscriptions.revenue)} />
                    </div>
                  </div>
                  {mship.top_packages && mship.top_packages.length > 0 && (
                    <Card className="p-5">
                      <h3 className="font-heading text-lg font-semibold mb-3">Pacchetti più venduti</h3>
                      <div className="space-y-2">
                        {mship.top_packages.map((t) => (
                          <div key={t.name} className="flex items-center justify-between text-sm border-b border-border pb-1.5 last:border-0">
                            <span>{t.name}</span><span className="font-medium">{num(t.count)}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            {/* RIEPILOGO ECONOMICO */}
            <TabsContent value="riepilogo" className="mt-4">
              <Card className="p-5">
                <h3 className="font-heading text-lg font-semibold mb-4">Riepilogo economico del periodo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2.5 text-sm">
                  {[
                    ["Fatturato lordo servizi", eur(s.services_gross)],
                    ["Sconti servizi", eur(s.services_discount)],
                    ["Fatturato netto servizi", eur(s.services_net)],
                    ["Costo materiali (consumi)", eur(s.material_cost)],
                    ["Commissioni servizi", eur(s.service_commission)],
                    ["Margine gestionale servizi", eur(s.service_margin)],
                    ["Margine %", s.margin_pct != null ? `${s.margin_pct}%` : "\u2014"],
                    ["Retail venduto (netto)", eur(s.products_net)],
                    ["Pacchetti venduti", `${num(s.packages_sold)} · ${eur(s.packages_value)}`],
                    ["Abbonamenti venduti", `${num(s.subscriptions_sold)} · ${eur(s.subscriptions_value)}`],
                    ["Gift Card vendute", eur(s.giftcards_sold_value)],
                    ["Gift Card utilizzate", eur(s.giftcards_used)],
                    ["Credito cliente utilizzato", eur(s.credit_used)],
                    ["Punti maturati / usati", `${num(s.points_earned)} / ${num(s.points_redeemed)}`],
                    ["Totale venduto", eur(s.total_sold)],
                    ["Totale incassato", eur(s.total_collected)],
                    ["Ticket medio", eur(s.avg_ticket)],
                    ["N. vendite", num(s.n_sales)],
                    ["Clienti / nuovi", `${num(s.n_clients)} / ${num(s.new_clients)}`],
                    ["Appuntamenti", num(s.n_appointments)],
                    ["Completati", num(s.completed)],
                    ["Cancellati / no-show", `${num(s.cancelled)} / ${num(s.no_shows)}`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-border/50 py-1.5 gap-4">
                      <span className="text-muted-foreground">{k}</span><span className="font-medium tabular text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-[11px] text-muted-foreground">Venduto, incassato e valore servizi sono mantenuti distinti per evitare doppie contabilizzazioni. Gift Card e pacchetti non sono conteggiati come servizi erogati.</div>
              </Card>
            </TabsContent>

            {/* SERVIZI */}
            <TabsContent value="servizi" className="mt-4">
              <Card className="p-0 overflow-hidden">
                <div className="p-4 border-b border-border"><h3 className="font-heading text-lg font-semibold">Dettaglio servizio per servizio</h3><p className="text-xs text-muted-foreground mt-0.5">Clicca una riga per il dettaglio delle prestazioni</p></div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Servizio</TableHead><TableHead>Categoria</TableHead><TableHead className="text-right">Qtà</TableHead>
                    <TableHead className="text-right">Netto</TableHead><TableHead className="text-right">Costo prod.</TableHead>
                    <TableHead className="text-right">Margine</TableHead><TableHead className="text-right">Marg.%</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.services.map((sv) => (
                      <TableRow key={sv.name} className="cursor-pointer" onClick={() => openDrill(sv.name)} data-testid={`svc-row-${sv.name}`}>
                        <TableCell className="font-medium">{sv.name}</TableCell>
                        <TableCell className="text-muted-foreground">{sv.category || "\u2014"}</TableCell>
                        <TableCell className="text-right tabular">{num(sv.qty)}</TableCell>
                        <TableCell className="text-right tabular font-medium">{eur(sv.net)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{eur(sv.cost)}</TableCell>
                        <TableCell className="text-right tabular font-medium text-[hsl(var(--hi-status-success))]">{eur(sv.margin)}</TableCell>
                        <TableCell className="text-right tabular text-xs">{sv.margin_pct != null ? `${sv.margin_pct}%` : "\u2014"}</TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    ))}
                    {data.services.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Nessun servizio nel periodo</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* SCONTI */}
            <TabsContent value="sconti" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <SummaryCard testid="disc-total" label="Totale scontato" value={eur(data.discounts.total)} />
                <SummaryCard testid="disc-count" label="Vendite con sconto" value={num(data.discounts.n_sales_with_discount)} />
                <SummaryCard testid="disc-avg" label="Sconto medio" value={eur(data.discounts.avg)} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-0 overflow-hidden">
                  <div className="p-4 border-b border-border"><h3 className="font-heading text-base font-semibold">Sconti per servizio</h3></div>
                  <Table><TableBody>
                    {Object.entries(data.discounts.by_service).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right tabular">{eur(v)}</TableCell></TableRow>)}
                    {Object.keys(data.discounts.by_service).length === 0 && <TableRow><TableCell className="text-center text-muted-foreground py-8">Nessuno sconto</TableCell></TableRow>}
                  </TableBody></Table>
                </Card>
                <Card className="p-0 overflow-hidden">
                  <div className="p-4 border-b border-border"><h3 className="font-heading text-base font-semibold">Sconti per collaboratore</h3></div>
                  <Table><TableBody>
                    {Object.entries(data.discounts.by_operator).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right tabular">{eur(v)}</TableCell></TableRow>)}
                    {Object.keys(data.discounts.by_operator).length === 0 && <TableRow><TableCell className="text-center text-muted-foreground py-8">Nessuno sconto</TableCell></TableRow>}
                  </TableBody></Table>
                </Card>
              </div>
            </TabsContent>

            {/* RETAIL & PAGAMENTI */}
            <TabsContent value="retail" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-0 overflow-hidden">
                <div className="p-4 border-b border-border"><h3 className="font-heading text-base font-semibold">Retail venduto</h3></div>
                <Table>
                  <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead className="text-right">Qtà</TableHead><TableHead className="text-right">Netto</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.products.map((p) => <TableRow key={p.name}><TableCell className="font-medium">{p.name}<div className="text-[11px] text-muted-foreground">{p.category}</div></TableCell><TableCell className="text-right tabular">{num(p.qty)}</TableCell><TableCell className="text-right tabular font-medium">{eur(p.net)}</TableCell></TableRow>)}
                    {data.products.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Nessuna vendita retail</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
              <Card className="p-5">
                <h3 className="font-heading text-base font-semibold mb-2">Metodi di pagamento</h3>
                {pieData.length === 0 ? <div className="text-sm text-muted-foreground py-12 text-center">Nessun dato</div> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                        {pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground flex justify-between"><span>Gift Card in circolazione</span><span className="tabular">{eur(data.wallet.giftcards_outstanding)}</span></div>
                <div className="text-xs text-muted-foreground flex justify-between mt-1"><span>Utilizzi pacchetti residui</span><span className="tabular">{num(data.wallet.packages_remaining_uses)}</span></div>
              </Card>
            </TabsContent>

            {/* INCASSI (Riepilogo pagamenti) */}
            <TabsContent value="incassi" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard testid="inc-sold" label="Venduto" value={eur(data.receipts.total_sold)} />
                <SummaryCard testid="inc-collected" label="Incassato" value={eur(data.receipts.total_collected)} />
                <SummaryCard testid="inc-tocollect" label="Da incassare" value={eur(data.receipts.to_collect)} hint={`${data.receipts.n_unpaid} vendite non saldate`} />
                <SummaryCard testid="inc-rate" label="Tasso di incasso" value={data.receipts.collect_rate != null ? `${data.receipts.collect_rate}%` : "\u2014"} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-0 overflow-hidden">
                  <div className="p-4 border-b border-border flex items-center gap-2"><Wallet className="h-4 w-4 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-base font-semibold">Incassato per metodo di pagamento</h3></div>
                  <Table>
                    <TableHeader><TableRow><TableHead>Metodo</TableHead><TableHead className="text-right">Incassato</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.receipts.by_method.map((m) => (
                        <TableRow key={m.method} data-testid={`inc-method-${m.method}`}>
                          <TableCell className="font-medium">{m.method}</TableCell>
                          <TableCell className="text-right tabular">{eur(m.amount)}</TableCell>
                          <TableCell className="text-right tabular text-muted-foreground">{data.receipts.total_collected ? `${Math.round(m.amount / data.receipts.total_collected * 100)}%` : "\u2014"}</TableCell>
                        </TableRow>
                      ))}
                      {data.receipts.by_method.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Nessun incasso nel periodo</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Card>
                <Card className="p-0 overflow-hidden">
                  <div className="p-4 border-b border-border flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-base font-semibold">Vendite non completamente saldate</h3></div>
                  <div className="max-h-[360px] overflow-y-auto hi-scroll">
                    <Table>
                      <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead className="text-right">Totale</TableHead><TableHead className="text-right">Incassato</TableHead><TableHead className="text-right">Residuo</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {data.receipts.unpaid.map((u) => (
                          <TableRow key={u.id} data-testid={`inc-unpaid-${u.id}`}>
                            <TableCell className="font-medium">{u.client_name}<div className="text-[11px] text-muted-foreground">{fmtDateTime(u.date)}</div></TableCell>
                            <TableCell className="text-right tabular">{eur(u.total)}</TableCell>
                            <TableCell className="text-right tabular text-muted-foreground">{eur(u.paid)}</TableCell>
                            <TableCell className="text-right tabular font-medium text-destructive">{eur(u.residuo)}</TableCell>
                          </TableRow>
                        ))}
                        {data.receipts.unpaid.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Tutte le vendite sono saldate</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* MARGINI PER OPERATORE */}
            <TabsContent value="margini" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard testid="mrg-services-net" label="Fatt. netto servizi" value={eur(s.services_net)} />
                <SummaryCard testid="mrg-material" label="Costo materiali" value={eur(s.material_cost)} />
                <SummaryCard testid="mrg-commission" label="Commissioni servizi" value={eur(s.service_commission)} />
                <SummaryCard testid="mrg-margin" label="Margine gestionale" value={eur(s.service_margin)} hint={s.margin_pct != null ? `${s.margin_pct}% sul netto` : undefined} />
              </div>
              {data.team.length > 0 && (
                <Card className="p-5">
                  <h3 className="font-heading text-base font-semibold mb-4">Margine per collaboratore</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.team} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `€${v}`} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                      <Bar dataKey="margin" name="Margine" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
              <Card className="p-0 overflow-hidden">
                <div className="p-4 border-b border-border flex items-center gap-2"><Percent className="h-4 w-4 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-lg font-semibold">Marginalità per collaboratore</h3></div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Collaboratore</TableHead>
                    <TableHead className="text-right">Fatt. servizi</TableHead>
                    <TableHead className="text-right">Costo materiali</TableHead>
                    <TableHead className="text-right">Commissioni</TableHead>
                    <TableHead className="text-right">Margine</TableHead>
                    <TableHead className="text-right">Margine %</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.team.map((t) => (
                      <TableRow key={t.operator_id} data-testid={`margin-row-${t.operator_id}`}>
                        <TableCell><span className="inline-flex items-center gap-2 font-medium"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />{t.name}</span></TableCell>
                        <TableCell className="text-right tabular font-medium">{eur(t.services_revenue)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{eur(t.material_cost)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{eur(t.commission)}</TableCell>
                        <TableCell className="text-right tabular font-medium text-[hsl(var(--hi-status-success))]">{eur(t.margin)}</TableCell>
                        <TableCell className="text-right tabular text-xs">{t.margin_pct != null ? `${t.margin_pct}%` : "\u2014"}</TableCell>
                      </TableRow>
                    ))}
                    {data.team.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessun dato nel periodo</TableCell></TableRow>}
                  </TableBody>
                </Table>
                <div className="p-4 text-[11px] text-muted-foreground border-t border-border">Margine per collaboratore = fatturato netto servizi − costo materiali − commissioni. I costi materiali derivano dai consumi reali registrati sulle vendite.</div>
              </Card>
            </TabsContent>

            {/* TEAM */}
            <TabsContent value="team" className="mt-4">
              <Card className="p-0 overflow-hidden">
                <div className="p-4 border-b border-border"><h3 className="font-heading text-lg font-semibold">Report team</h3></div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Collaboratore</TableHead><TableHead className="text-right">Appunt.</TableHead><TableHead className="text-right">Servizi</TableHead>
                    <TableHead className="text-right">Fatt. servizi</TableHead><TableHead className="text-right">Retail</TableHead>
                    <TableHead className="text-right">Margine</TableHead>
                    <TableHead className="text-right">Ticket medio</TableHead><TableHead className="text-right">Sconti</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.team.map((t) => (
                      <TableRow key={t.operator_id} data-testid={`team-row-${t.operator_id}`}>
                        <TableCell><span className="inline-flex items-center gap-2 font-medium"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />{t.name}</span></TableCell>
                        <TableCell className="text-right tabular">{num(t.appointments)}</TableCell>
                        <TableCell className="text-right tabular">{num(t.services_count)}</TableCell>
                        <TableCell className="text-right tabular font-medium">{eur(t.services_revenue)}</TableCell>
                        <TableCell className="text-right tabular">{eur(t.retail_revenue)}</TableCell>
                        <TableCell className="text-right tabular text-[hsl(var(--hi-status-success))]">{eur(t.margin)}</TableCell>
                        <TableCell className="text-right tabular">{eur(t.avg_ticket)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{eur(t.discount)}</TableCell>
                      </TableRow>
                    ))}
                    {data.team.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Nessun dato team</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* ANNUALE */}
            {data.monthly && (
              <TabsContent value="annuale" className="mt-4 space-y-4">
                <Card className="p-5">
                  <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Trend mensile</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                      <Legend />
                      <Bar dataKey="services_net" name="Servizi" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="products_net" name="Retail" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-0 overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Mese</TableHead><TableHead className="text-right">Venduto</TableHead><TableHead className="text-right">Incassato</TableHead><TableHead className="text-right">Servizi</TableHead><TableHead className="text-right">Retail</TableHead><TableHead className="text-right">Clienti</TableHead><TableHead className="text-right">Ticket</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.monthly.map((m) => <TableRow key={m.month}><TableCell className="font-medium">{m.label}</TableCell><TableCell className="text-right tabular">{eur(m.total_sold)}</TableCell><TableCell className="text-right tabular">{eur(m.total_collected)}</TableCell><TableCell className="text-right tabular">{eur(m.services_net)}</TableCell><TableCell className="text-right tabular">{eur(m.products_net)}</TableCell><TableCell className="text-right tabular">{num(m.n_clients)}</TableCell><TableCell className="text-right tabular">{eur(m.avg_ticket)}</TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}

      {/* Drill-down prestazioni */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl" data-testid="drill-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{drill?.service} · {drill?.count} prestazioni · {eur(drill?.net_total || 0)}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Operatore</TableHead><TableHead className="text-right">Netto</TableHead><TableHead>Pagamento</TableHead></TableRow></TableHeader>
              <TableBody>
                {drill?.rows.map((r, i) => <TableRow key={i}><TableCell className="text-xs whitespace-nowrap">{fmtDateTime(r.date)}</TableCell><TableCell>{r.client_name}</TableCell><TableCell>{r.operator}</TableCell><TableCell className="text-right tabular font-medium">{eur(r.net)}</TableCell><TableCell><Badge variant="outline">{r.payment || "—"}</Badge></TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
